import React from 'react';

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

class ReactErrorBoundary extends React.Component<
  React.PropsWithChildren<Record<string, unknown>>,
  ErrorBoundaryState
> {
  constructor(props: React.PropsWithChildren<Record<string, unknown>>) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    console.error('🚨 React Error Boundary caught error:', error);
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('🚨 React Error Details:', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack
    });
    
    // Log specific React error types
    if (error.message.includes('Element type is invalid')) {
      console.error('🔥 CRITICAL: React Error #130 - Invalid Element Type');
      console.error('This usually means a number/string is being rendered as a component');
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-red-50">
          <div className="max-w-md p-6 bg-white rounded-lg shadow-lg border-l-4 border-red-500">
            <h2 className="text-lg font-semibold text-red-800 mb-2">
              ⚠️ خطأ في التطبيق / Application Error
            </h2>
            <p className="text-sm text-red-600 mb-4">
              حدث خطأ غير متوقع. تم تسجيل التفاصيل في وحدة التحكم.
            </p>
            <p className="text-xs text-red-500 mb-4">
              An unexpected error occurred. Details have been logged to console.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
            >
              إعادة التحميل / Reload
            </button>
            
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-4">
                <summary className="cursor-pointer text-sm font-medium text-red-700">
                  Error Details (Development Only)
                </summary>
                <pre className="text-xs bg-red-100 p-2 rounded mt-2 overflow-auto max-h-32">
                  {this.state.error.stack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ReactErrorBoundary;